import { useCallback, useState, useLayoutEffect, MouseEvent as ReactMouseEvent } from 'react';
import { unstable_batchedUpdates as batchedUpdates } from 'react-dom';

export interface DragCallbackParams {
    dx: number;
    dy: number;
    pageX: number;
    pageY: number;
    clientX: number;
    clientY: number;
}

type DragCallback = (params: DragCallbackParams) => void;

export const useDrag = ({
    onDrag,
    onDragStart,
    onDragEnd,
}: {
    onDrag: DragCallback;
    onDragStart?: DragCallback;
    onDragEnd?: DragCallback;
}) => {
    const [startData, setStartData] = useState<{
        startX: number;
        startY: number;
    } | null>(null);

    const onMouseDown = useCallback(
        (e: ReactMouseEvent) => {
            setStartData({
                startX: e.pageX,
                startY: e.pageY,
            });
            onDragStart?.({
                dx: 0,
                dy: 0,
                pageX: e.pageX,
                pageY: e.pageY,
                clientX: e.clientX,
                clientY: e.clientY,
            });
        },
        [onDragStart]
    );

    useLayoutEffect(() => {
        if (startData) {
            const onMouseMove = (e: MouseEvent) => {
                const dx = e.pageX - startData.startX;
                const dy = e.pageY - startData.startY;
                batchedUpdates(() => {
                    onDrag({
                        dx,
                        dy,
                        pageX: e.pageX,
                        pageY: e.pageY,
                        clientX: e.clientX,
                        clientY: e.clientY,
                    });
                });
            };
            document.addEventListener('mousemove', onMouseMove);
            return () => {
                document.removeEventListener('mousemove', onMouseMove);
            };
        }
    }, [startData, onDrag]);

    useLayoutEffect(() => {
        if (startData) {
            const onMouseUp = (e: MouseEvent) => {
                const dx = e.pageX - startData.startX;
                const dy = e.pageY - startData.startY;
                batchedUpdates(() => {
                    setStartData(null);
                    onDragEnd?.({
                        dx,
                        dy,
                        pageX: e.pageX,
                        pageY: e.pageY,
                        clientX: e.clientX,
                        clientY: e.clientY,
                    });
                });
            };
            document.addEventListener('mouseup', onMouseUp);
            return () => {
                document.removeEventListener('mouseup', onMouseUp);
            };
        }
    }, [startData, onDragEnd]);

    return {
        onMouseDown,
        dragging: startData !== null,
    };
};
